import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchData, useCreateData, useUpdateData } from 'services/queries';
import Card from 'components/card';
import { TextField, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { BsArrowLeft } from "react-icons/bs";
import { CATEGORY_CHOICES } from 'constants';

const AdditionalCostSubcategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    description: '',
  });
  
  const [errors, setErrors] = useState({});

  // Fetch existing data if editing
  const { data: existingData } = useFetchData(
    `additional-cost/subcategory/${id}/`,
    true,
    isEdit
  );

  // Set up mutations
  const createMutation = useCreateData('additional-cost/subcategory');
  const updateMutation = useUpdateData('additional-cost/subcategory');

  useEffect(() => {
    if (existingData) {
      setFormData({
        category: existingData.category || '',
        name: existingData.name || '',
        description: existingData.description || '',
      });
    }
  }, [existingData]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.name) newErrors.name = 'Name is required';
    if (formData.name && formData.name.length > 100) newErrors.name = 'Name must be less than 100 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id, newData: formData });
        // Stay on the same page after update
      } else {
        const result = await createMutation.mutateAsync(formData);
        // Redirect to edit page after create
        if (result?.id) {
          navigate(`/admin/settings/additional-costs/edit/${result.id}`);
        }
      }
    } catch (error) {
      if (error?.response?.data) {
        setErrors(error.response.data);
      }
    }
  };

  const handleCancel = () => {
    navigate('/admin/settings/additional-costs');
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="mt-5 grid h-full">
        <div className="flex items-center gap-3">
          <Button
            startIcon={<BsArrowLeft />}
            onClick={handleCancel}
            variant="text"
            className="!text-gray-600"
          >
            Back
          </Button>
        </div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white mt-3">
          {isEdit ? 'Edit Subcategory' : 'New Subcategory'}
        </h1>
        <p className="mt-2 text-base text-gray-600">
          {isEdit ? 'Update the subcategory details' : 'Create a new subcategory for additional costs'}
        </p>
      </div>

      <Card extra={'w-full h-full bg-white p-6'}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormControl fullWidth required error={!!errors.category}>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
                disabled={isEdit} // Category cannot be changed when editing
              >
                {CATEGORY_CHOICES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <span className="text-red-500 text-sm mt-1">{errors.category}</span>
              )}
            </FormControl>

            <TextField
              name="name"
              label="Subcategory Name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              error={!!errors.name}
              helperText={errors.name}
            />
          </div>

          <TextField
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description}
          />

          <div className="flex justify-end gap-4 mt-6">
            <Button
              variant="outlined"
              onClick={handleCancel}
              className="!border-gray-300 !text-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AdditionalCostSubcategoryForm;